/**
 * AIResearchTool - AI-powered speaker and sponsor research for event planners
 * Integrates with Gemini AI to provide structured information
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Search, User, Building2, Linkedin, Twitter, Globe,
  Loader2, Sparkles, BookOpen, MessageSquare, Copy, Check,
  ExternalLink, AlertCircle
} from 'lucide-react';
import { researchAPI } from '@/lib/api';

const AIResearchTool = () => {
  const [searchType, setSearchType] = useState('speaker');
  const [searchQuery, setSearchQuery] = useState('');
  const [result, setResult] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [copiedField, setCopiedField] = useState(null);

  const researchMutation = useMutation({
    mutationFn: (name) => {
      return searchType === 'speaker' 
        ? researchAPI.speaker(name)
        : researchAPI.sponsor(name);
    },
    onSuccess: (response) => {
      const data = response.data;
      setResult(data);
      // Add to history
      setSearchHistory(prev => [{
        query: searchQuery,
        type: searchType,
        result: data,
        timestamp: new Date().toLocaleTimeString()
      }, ...prev.slice(0, 9)]);
      toast.success(`Research completed for ${searchQuery}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Research failed. Please try again.');
      setResult(null);
    }
  });

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a name or company to research');
      return;
    }
    researchMutation.mutate(searchQuery.trim());
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copied to clipboard');
  };

  const loadFromHistory = (item) => {
    setSearchQuery(item.query);
    setSearchType(item.type);
    setResult(item.result);
  };

  return (
    <div className="space-y-6" data-testid="ai-research-tool">
      {/* Search Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-500" />
            AI Research Assistant
          </CardTitle>
          <CardDescription>
            Research speakers and sponsors using AI to get structured bios, social links, and talking points
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Type Toggle */}
          <Tabs value={searchType} onValueChange={setSearchType}>
            <TabsList className="grid w-full grid-cols-2 max-w-xs">
              <TabsTrigger value="speaker" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Speaker
              </TabsTrigger>
              <TabsTrigger value="sponsor" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Sponsor
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Search Input */}
          <div className="flex gap-2">
            <Input
              placeholder={searchType === 'speaker' 
                ? "Enter speaker name (e.g., Satya Nadella)" 
                : "Enter company name (e.g., Microsoft)"}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
              data-testid="research-input"
            />
            <Button 
              onClick={handleSearch}
              disabled={researchMutation.isPending || !searchQuery.trim()}
              data-testid="research-submit"
            >
              {researchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-2">Research</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {researchMutation.isPending && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium">Researching {searchQuery}...</p>
            <p className="text-sm text-slate-500">AI is gathering information</p>
          </CardContent>
        </Card>
      )}

      {result && !researchMutation.isPending && (
        <Card data-testid="research-result">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {result.type === 'speaker' ? (
                    <User className="h-5 w-5" />
                  ) : (
                    <Building2 className="h-5 w-5" />
                  )}
                  {result.name}
                </CardTitle>
                {result.title && (
                  <CardDescription>{result.title}</CardDescription>
                )}
              </div>
              <Badge variant={result.type === 'speaker' ? 'default' : 'secondary'}>
                {result.type}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Bio Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-slate-500" />
                  Biography
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(result.bio, 'bio')}
                >
                  {copiedField === 'bio' ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-lg">
                {result.bio}
              </p>
            </div>

            {/* Company */}
            {result.company && (
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  Company/Organization
                </h4>
                <p className="text-slate-600">{result.company}</p>
              </div>
            )}

            {/* Expertise */}
            {result.expertise && result.expertise.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Areas of Expertise</h4>
                <div className="flex flex-wrap gap-2">
                  {result.expertise.map((exp, i) => (
                    <Badge key={i} variant="outline">
                      {exp}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Social Links */}
            {result.social_links && (
              <div className="space-y-2">
                <h4 className="font-medium">Social Links</h4>
                <div className="flex flex-wrap gap-2">
                  {result.social_links.linkedin && (
                    <Button variant="outline" size="sm" asChild>
                      <a 
                        href={result.social_links.linkedin.startsWith('http') 
                          ? result.social_links.linkedin 
                          : `https://${result.social_links.linkedin}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Linkedin className="h-4 w-4 mr-2 text-blue-600" />
                        LinkedIn
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                  )}
                  {result.social_links.twitter && (
                    <Button variant="outline" size="sm" asChild>
                      <a 
                        href={result.social_links.twitter.startsWith('http') 
                          ? result.social_links.twitter 
                          : `https://twitter.com/${result.social_links.twitter.replace('@', '')}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Twitter className="h-4 w-4 mr-2 text-sky-500" />
                        Twitter
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                  )}
                  {result.social_links.website && (
                    <Button variant="outline" size="sm" asChild>
                      <a 
                        href={result.social_links.website.startsWith('http') 
                          ? result.social_links.website 
                          : `https://${result.social_links.website}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Globe className="h-4 w-4 mr-2 text-green-600" />
                        Website
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Talking Points */}
            {result.talking_points && result.talking_points.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-slate-500" />
                    Suggested Talking Points
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(result.talking_points.join('\n'), 'points')}
                  >
                    {copiedField === 'points' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <ul className="space-y-2">
                  {result.talking_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600">
                      <span className="text-blue-500 mt-1">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Logo Search Suggestion */}
            {result.suggested_logo_search && (
              <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                <span className="font-medium">Logo search tip:</span> Search for &ldquo;{result.suggested_logo_search}&rdquo; to find official logos
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Searches</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {searchHistory.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => loadFromHistory(item)}
                    className="w-full text-left p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.type === 'speaker' ? (
                          <User className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Building2 className="h-4 w-4 text-slate-400" />
                        )}
                        <span className="font-medium">{item.query}</span>
                        <Badge variant="outline" className="text-xs">
                          {item.type}
                        </Badge>
                      </div>
                      <span className="text-xs text-slate-400">{item.timestamp}</span>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!result && !researchMutation.isPending && searchHistory.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-600">Start Researching</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Enter a speaker or sponsor name above to get AI-powered insights including 
              bio, social links, expertise areas, and suggested talking points.
            </p>
          </CardContent>
        </Card>
      )}

      {/* API Key Warning */}
      <div className="text-xs text-slate-400 flex items-center gap-1 justify-center">
        <AlertCircle className="h-3 w-3" />
        Research powered by AI. Results may need verification.
      </div>
    </div>
  );
};

export default AIResearchTool;
